import { Injectable } from "@angular/core";
import { HttpClient, HttpContext } from "@angular/common/http";
import { Observable, Subject, BehaviorSubject, forkJoin } from "rxjs";
import { map } from "rxjs/operators";
import { HANDLES_OWN_ERRORS } from "../interceptor/http-context-tokens";
import {
  DOCUMENT_TYPE_VALUE_LIST_TYPE,
  OBLIGATION_VALUE_LIST_ATTRIBUTES,
} from "../../pages/incident-tracker/compliance/compliance-attributes";
import {
  AmendmentDetail,
  AmendmentHistory,
  DocumentAmendment,
  PaginatedResponse,
} from "../../pages/incident-tracker/compliance/compliance-amendments/compliance-amendments.model";
import { DocumentReference } from "../../pages/incident-tracker/compliance/compliance-references/compliance-references.model";
import { ObligationHistory } from "../../pages/incident-tracker/compliance/obligation-history/obligation-history.model";

// export interface ActionStatusPayload {
//   action: number;
//   assignee: number;
//   status: "approved" | "rejected";
// }

@Injectable({
  providedIn: "root",
})
export class ComplianceService {
  private readonly reloadComplianceListSource = new Subject<void>();
  reloadComplianceList$ = this.reloadComplianceListSource.asObservable();
  constructor(private readonly http: HttpClient) {}
  public complianceModeSubject = new BehaviorSubject<boolean>(false);
  complianceMode$ = this.complianceModeSubject.asObservable();
  private activeComplianceSubTab$ = new BehaviorSubject<
    "obligations" | "setup"
  >("obligations");
  setActiveComplianceSubTab(tab: "obligations" | "setup") {
    this.activeComplianceSubTab$.next(tab);
  }
  getActiveComplianceSubTab() {
    return this.activeComplianceSubTab$.value;
  }

  setcomplianceMode(isCompliance: boolean) {
    this.complianceModeSubject.next(isCompliance);
  }

  getcomplianceMode(): boolean {
    return this.complianceModeSubject.getValue();
  }

  triggerReload() {
    this.reloadComplianceListSource.next();
  }

  // Users
  getFilterOwner(moduleName: string = "") {
    return this.http.get(`users/filter-user/?users=${moduleName}`);
  }

  // ------------------ Compliance Documents ------------------
  getComplianceDocuments(params: any = {}): Observable<any> {
    return this.http.get("grc/compliance-documents/", { params });
  }

  getComplianceDocumentById(id: number): Observable<any> {
    return this.http.get(`grc/compliance-documents/${id}/`);
  }

  createComplianceDocument(payload: any): Observable<any> {
    return this.http.post("grc/compliance-documents/", payload);
  }

  updateComplianceDocument(id: number, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-documents/${id}/`, payload);
  }

  /**
   * Distillation progress of a single uploaded document. Returns a `status` of
   * "pending" | "processing" | "completed" | "failed" | "blocked", or "null"
   * when the document has no processing row yet.
   *
   * "blocked" is paused, not dead: the run stopped on something the tenant can
   * clear (eg. AI credits running out) and everything extracted so far is kept, so
   * it resumes from that point instead of being uploaded again. Only "failed"
   * means the document has to be re-uploaded.
   */
  getComplianceDocumentStatus(
    documentId: number | string,
    type: string = "distillation"
  ): Observable<any> {
    return this.http.get(
      `grc/compliance-documents/status/?document_id=${documentId}&type=${type}`
    );
  }

  /**
   * Same as getComplianceDocumentStatus, for many documents in one request, so
   * a page watching several in-flight uploads polls once per tick rather than
   * once per document. Resolves to the `results` array.
   */
  getComplianceDocumentStatuses(
    documentIds: Array<number | string>,
    type: string = "distillation"
  ): Observable<any[]> {
    const ids = documentIds.join(",");
    return this.http
      .get(`grc/compliance-documents/status/?document_ids=${ids}&type=${type}`)
      .pipe(map((resp: any) => resp?.results ?? []));
  }

  /**
   * The document's PDF: from S3 for an internal document, otherwise fetched
   * live from the regulator's website. A failure carries a displayable
   * `error` message - inside a Blob, given the response type - which the
   * caller shows itself.
   */
  getDocumentPDF(id: number) {
    return this.http.get(`grc/compliance-documents/${id}/download_document/`, {
      responseType: "blob",
      context: new HttpContext().set(HANDLES_OWN_ERRORS, true),
    });
  }

  // ------------------- (Special type of Compliance Document) ----------
  getCommplianceDocTypes() {
    return this.http.get(`valuelists/?type=${DOCUMENT_TYPE_VALUE_LIST_TYPE}`);
  }

  // ------------------ Value Lists ------------------
  getValueListOptions(type: string): Observable<any> {
    return this.http.get(`valuelists/?type=${type}`);
  }

  /**
   * Options for every ValueList-backed obligation attribute, keyed by the
   * attribute's field name. Value list types shared by two attributes (e.g.
   * category / sub_category) are only fetched once.
   */
  getObligationAttributeOptions(): Observable<{ [field: string]: any[] }> {
    const requests: { [type: string]: Observable<any> } = {};
    OBLIGATION_VALUE_LIST_ATTRIBUTES.forEach((attr) => {
      requests[attr.valueListType] ??= this.getValueListOptions(
        attr.valueListType
      );
    });

    return forkJoin(requests).pipe(
      map((optionsByType: any) => {
        const optionsByField: { [field: string]: any[] } = {};
        OBLIGATION_VALUE_LIST_ATTRIBUTES.forEach((attr) => {
          optionsByField[attr.field] = optionsByType[attr.valueListType] ?? [];
        });
        return optionsByField;
      })
    );
  }

  // ------------------ Amendments ------------------
  /**
   * The amending instruments that changed document `documentId`'s
   * obligations, newest first, each with its rows for that document.
   */
  getDocumentAmendments(
    documentId: number,
    page: number,
    limit: number
  ): Observable<PaginatedResponse<DocumentAmendment>> {
    return this.http.get<PaginatedResponse<DocumentAmendment>>(
      "grc/compliance-amendments/",
      {
        params: {
          target_document: String(documentId),
          page: String(page),
          limit: String(limit),
        },
      }
    );
  }

  /** One amending instrument with a row per obligation it added, changed or ended. */
  getAmendment(id: number): Observable<AmendmentDetail> {
    return this.http.get<AmendmentDetail>(`grc/compliance-amendments/${id}/`);
  }

  /** The instruments compliance document `documentId` cites, with where it cites them. */
  getDocumentReferences(documentId: number): Observable<DocumentReference[]> {
    return this.http.get<DocumentReference[]>(
      `grc/compliance-references/${documentId}/`
    );
  }

  /** The amendment history of the obligation `clauseId` (any of its versions) belongs to. */
  getAmendmentHistory(clauseId: number): Observable<AmendmentHistory> {
    return this.http.get<AmendmentHistory>(
      `grc/compliance-obligation-versions/${clauseId}/`
    );
  }

  // ------------------ Compliance Clauses ------------------
  getComplianceClauses(params: any = {}): Observable<any> {
    return this.http.get("grc/compliance-clauses/", { params });
  }

  getComplianceClausesNames(): Observable<any> {
    return this.http.get("grc/compliance-clauses/names");
  }

  getComplianceClauseById(id: number): Observable<any> {
    return this.http.get(`grc/compliance-clauses/${id}/`);
  }

  deleteComplianceClause(id: number): Observable<any> {
    return this.http.delete(`grc/compliance-clauses/${id}/`);
  }

  createComplianceClause(payload: any): Observable<any> {
    return this.http.post("grc/compliance-clauses/", payload);
  }

  updateComplianceClause(id: number, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-clauses/${id}/`, payload);
  }

  /** The change history of the obligation version `clauseId`: who changed what on it, and when. */
  getObligationHistory(clauseId: number): Observable<ObligationHistory> {
    return this.http.get<ObligationHistory>(
      `grc/compliance-clauses/${clauseId}/history/`
    );
  }

  getFilteredComplianceClauses(params: string): Observable<any> {
    return this.http.get(`grc/compliance-sections/clauses/${params}`);
  }

  getComplianceKpiMetrics(params: string): Observable<any> {
    return this.http.get(`grc/compliance-sections/clauses-metrics/${params}`);
  }

  getComplianceStatus() {
    return this.http.get("base_value/?type=compliance_obligation_status");
  }

  getobligationSystemIdList(): Observable<any> {
    return this.http.get("grc/compliance-clauses/system-id/");
  }

  // ------------------ Website Configurations ------------------
  /** internalOnly: only internal sources, the ones documents can be uploaded to. */
  getWebsiteConfigurationsNames(internalOnly = false): Observable<any> {
    return this.http.get(
      `grc/website-configurations/names/${internalOnly ? "?is_internal=true" : ""}`
    );
  }

  getWebsiteConfigurations(params: string = ""): Observable<any> {
    return this.http.get(`grc/website-configurations/${params}`);
  }

  getProject() {
    return this.http.get(`projects/`);
  }

  getWebsiteConfigurationById(id: number): Observable<any> {
    return this.http.get(`grc/website-configurations/${id}/`);
  }

  createWebsiteConfiguration(payload: any): Observable<any> {
    return this.http.post("grc/website-configurations/", payload);
  }

  updateWebsiteConfiguration(id: number, payload: any): Observable<any> {
    return this.http.patch(`grc/website-configurations/${id}/`, payload);
  }

  // ------------------ Regulators ------------------
  getRegulators(): Observable<any> {
    return this.http.get("grc/compliance-regulators/");
  }

  // ------------------ Compliance Tags ------------------
  getComplianceTags(): Observable<any> {
    return this.http.get("grc/compliance-tags/");
  }

  saveComplianceTags(tags: string[], available: string[]): Observable<any> {
    return this.http.post("grc/compliance-tags/", { tags, available });
  }

  // -------------------Compliance Risks-------------

  getRecommendedComplianceRisks(clauseId: number) {
    return this.http.get(
      `grc/compliance-clauses/${clauseId}/recommended-risks/`
    );
  }
  getComplianceRisks(clauseId: number) {
    return this.http.get(`grc/compliance-clause-risks/?clause_id=${clauseId}`);
  }

  selectComplianceRisks(payload: any, moduleName: string = "") {
    return this.http.post(
      `grc/compliance-clause-risks/select-risk/?module=${moduleName}`,
      payload
    );
  }

  getPerformerReviwerMapping(): Observable<any> {
    return this.http.get(`grc/compliance-performer-reviewer-master/`);
  }

  /** Every user, unfiltered — the option list for a brand new FPR mapping. */
  getAllUsers(): Observable<any> {
    return this.http.get("users/filter-user/");
  }

  /**
   * Adds and removes master function-performer-reviewer mappings in one call.
   * `performer_reviewer` holds the new mappings, `deleted_performer_reviewer`
   * the ones to remove.
   */
  savePerformerReviewerMaster(payload: {
    performer_reviewer: any[];
    deleted_performer_reviewer: any[];
  }): Observable<any> {
    return this.http.post(`grc/compliance-performer-reviewer-master/`, payload);
  }

  getPerformers(): Observable<any> {
    return this.http.get(
      `grc/compliance-performer-reviewer-master/performers-list/`
    );
  }

  getReviewers(): Observable<any> {
    return this.http.get(
      `grc/compliance-performer-reviewer-master/reviewers-list/`
    );
  }

  // ------------------ Controls ------------------
  getObligationControls(id: any): Observable<any> {
    return this.http.get(`grc/compliance-controls/${id}`);
  }

  getObligationControlsList(qparams: string): Observable<any> {
    return this.http.get(`grc/compliance-controls/${qparams}`);
  }

  getObligationControlTasks(compliance_clause: any): Observable<any> {
    return this.http.get(
      `grc/compliance-controls/tasks/?compliance_clause=${compliance_clause}`
    );
  }

  createObligationControl(payload: any): Observable<any> {
    return this.http.post("grc/compliance-controls/", payload);
  }

  updateObligationControl(controlId: number, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-controls/${controlId}/`, payload);
  }

  /** Stops the control's tasks from recurring; activating it again resumes them. */
  deactivateControl(controlId: number): Observable<any> {
    return this.http.post(`grc/compliance-controls/${controlId}/deactivate/`, {});
  }

  getAiroRepos() {
    return this.http.get("grc/compliance-controls/airo-repos");
  }

  // ------------------ Tasks ------------------
  getObligationControlTask(id: any): Observable<any> {
    return this.http.get(`grc/compliance-tasks/${id}/`);
  }

  createControlTask(payload: any): Observable<any> {
    return this.http.post("grc/compliance-tasks/", payload);
  }

  /**
   * What activating the controls would do, without doing it (dry run): refused
   * with the reason, or per control the task it moves or follows and the
   * controls of older versions it deactivates.
   */
  previewControlActivation(payload: any): Observable<any> {
    return this.http.post("grc/compliance-tasks/?dry_run=true", payload);
  }

  getTaskStatusOptions() {
    return this.http.get("valuelists/?type=compliance_task_status");
  }

  getBulkUpdateTaskStatusOptions() {
    return this.http.get("valuelists/?type=compliance_bulk_update_task_status");
  }

  updateControlTask(id: any, payload: any): Observable<any> {
    return this.http.patch(`grc/compliance-tasks/${id}/`, payload);
  }

  // ------------------ Performer-Reviewer ------------------
  getPerformerReviewer(payload: any): Observable<any> {
    return this.http.get("grc/compliance-performer-reviewer/", payload);
  }

  getComplianceFrequencyOptions() {
    return this.http.get("valuelists/?type=compliance_frequency");
  }

  getControlTypes() {
    return this.http.get("valuelists/?type=compliance_control_type");
  }

  getSectionDetails(id: any): Observable<any> {
    return this.http.get(`grc/compliance-sections/${id}/`);
  }
  // ------------------ actor-status-options ------------------
  getStatusOptions(actor: string) {
    return this.http.get(
      `action-tracker/status-options/?type=compliance&actor=${actor}`
    );
  }

  // ------------------ navigation ------------------

  getAdjacentClauses(
    clauseId: number,
    documentId: number,
    filterQuery: string
  ): Observable<any> {
    return this.http.get(
      `grc/compliance-clauses/navigation/?document=${documentId}&obligation_id=${clauseId}${filterQuery}`
    );
  }

  getFunctions(): Observable<any> {
    return this.http.get("valuelists/?type=enterprise_function_compliance");
  }

  // ------------------ bulk-update for tasks of controls which are of control-type 'checklist' ------------------

  bulkCloseTasks(closureType: any, payload: any) {
    return this.http.patch(
      `grc/compliance-tasks/bulk-update-status/?completion_style=${closureType}`,
      payload
    );
  }

  getComplianceRegulators(): Observable<any> {
    return this.http.get("grc/compliance-store/regulators/");
  }

  getComplianceRiskTypes(): Observable<any> {
    return this.http.get("grc/compliance-risk-type/");
  }

  getComplianceStoreCatalog(): Observable<any> {
    return this.http.get("grc/compliance-store/regulators-with-documents/");
  }

  fetchComplianceStoreData(documentUuids: string[]) {
    return this.http.post(`grc/compliance-store/fetch-document-data/`, {
      document_uuids: documentUuids,
    });
  }

  syncDocuments(documentUuids: string[]) {
    return this.http.post(`grc/compliance-store/sync/`, {
      document_uuids: documentUuids,
    });
  }
}
